﻿import groupBy from 'lodash.groupby';

import {insuranceGroupSettings} from './insuranceGroupSettings';

// get meta data for insurance
export function getInsuranceMeta(insuranceName) {
    const store = window.__bazStore.getState();
    let data = store.insurancePageData.insurancesByName
        || store.preregistrationPageData.insurancesByName
        || store.startPageData.insurancesByName;

    return data[insuranceName];
}

// get insurance quote data
export function getQuoteByName(insuranceName) {
    let data = window.__bazStore.getState().insurancePageData;
    if (!data) {
        return null;
    }

    let insuranceMeta = data.insurancesByName[insuranceName];
    let quoteIndex = data.quotesByGroupName.findIndex(q => q.groupName == insuranceMeta.groupName);
    let quote = data.quotesByGroupName[quoteIndex].insurances.find(i => i.insuranceName == insuranceName);

    return quote;
}

// get insurance possession data by possession id
export function getPossessionById(possessionId) {
    let data = window.__bazStore.getState().insurancePageData;

    if (!data || !data.possessions) {
        return null;
    }

    return data.possessions.find(i => i.possessionId == possessionId);
}

// get insurance possession data by insurance object id
export function getPossessionByInsuranceObjectId(insuranceObjectId) {
    let data = window.__bazStore.getState().insurancePageData;

    if (!data || !data.possessions) {
        return null;
    }

    return data.possessions.find(i => i.insuranceObjectId == insuranceObjectId);
}


export function getPremiumLabel(key) {
    const premiumLabels = {
        monthly: 'Månadspris',
        yearly: 'Årspris',
        onetime: 'Engångspris'
    };

    return key in premiumLabels ? premiumLabels[key] : 'Okänd premieperiod';
}

export function getChildGenderLabel(key) {
    let k = typeof key !== 'undefined' && key
        ? key.toLowerCase()
        : '';

    const genderLabels = {
        male: 'Pojke',
        female: 'Flicka',
        other: 'Annat'
    };

    return k in genderLabels ? genderLabels[k] : 'Okänt';
}

export function getAnimalGenderLabel(key) {
    let k = typeof key !== 'undefined' && key
        ? key.toLowerCase()
        : '';

    const genderLabels = {
        male: 'Hane',
        female: 'Hona',
        castrated: 'Kastrerad'
    };

    return k in genderLabels ? genderLabels[k] : 'Okänt';
}
export function getAnimalTypeLabel(key) {
    let k = typeof key !== 'undefined' && key
        ? key.toLowerCase()
        : '';

    const typeLabels = {
        cat: 'Katt',
        dog: 'Hund',
        smallanimal: 'Smådjur'
    };

    return k in typeLabels ? typeLabels[k] : 'Okänt';
}

export const fixCamelCasedGroupKeys = (metaData) => Object.keys(metaData).reduce((acc, k) => {
    acc[metaData[k].groupName] = metaData[k];
    return acc;
}, {});



// sort insurances by name
export function sortInsurancesByName(insurances) {
    return insurances.reduce((sorted, insurance) => {
        let name = typeof insurance.name !== 'undefined'
            ? insurance.name
            : insurance.insuranceName;

        sorted[name] = insurance;
        return sorted;
    }, {});
}

// sort insurances by group name
export function sortInsurancesByGroupName(insurances, metaData = null) {
    const getGroupName = ins => {
        if (typeof ins.groupName === 'undefined' || !ins.groupName) {
            return ins.insuranceName in metaData
                && typeof metaData[ins.insuranceName].groupName != 'undefined'
                && metaData[ins.insuranceName].groupName
                    ? metaData[ins.insuranceName].groupName
                    : ins.insuranceName.split('-')[0];
        }

        return ins.groupName;
    };

    return insurances.reduce((acc, insurance) => {
        let existingGroup = acc.filter((addedGroup) => addedGroup.groupName === getGroupName(insurance));

        if (existingGroup.length) {
                existingGroup[0].insurances.push(metaData ? {
                    ...metaData[insurance.insuranceName],
                    ...insurance

                } : insurance);
        } else {
            let groupName = getGroupName(insurance);

            acc.push({
                groupName,
                displayName: groupName in insuranceGroupSettings ? insuranceGroupSettings[groupName].displayName : null,
                insurances: metaData ? [{
                    ...metaData[insurance.insuranceName],
                    ...insurance
                }] : [insurance]
            });
        }

        return acc;
    }, []);
}

// sort ordered insurances by completed/pending/failed status (pending = in wait for health declaration etc.)
// also attach quote- and meta-data
export function sortOrders(orders, validationResults, orderSystemDown) {
    let sorted = sortInsurancesByGroupName(orders).reduce((sorted, group) => {
        let ordersWithMeta = group.insurances.map(insurance => {
            return {
                ...getInsuranceMeta(insurance.insuranceName),
                ...getQuoteByName(insurance.insuranceName),
                ...insurance
            };
        });

        const orderHasFailed = insuranceName => {
            let errors = validationResults
                .filter(validation =>
                    validation.insuranceName == insuranceName
                    && ((validation.validationErrors && validation.validationErrors.length > 0)
                        || (validation.result && !validation.result.success)));

            return errors ? errors.length > 0 : false;
        };

        const getValidationErrors = insuranceName => {
            let errors = validationResults.filter(validation => validation.insuranceName == insuranceName);
            return errors.length > 0 ? errors[0].validationErrors : {};
        };

        let completedOrders = ordersWithMeta.filter(insurance =>
            !insurance.requiresHealthDeclaration && !insurance.isQuoteRequest && !orderHasFailed(insurance.insuranceName)
        );

        let pendingOrders = ordersWithMeta.filter(insurance =>
            (insurance.requiresHealthDeclaration || insurance.isQuoteRequest) && !orderHasFailed(insurance.insuranceName)
        );

        let failedOrders = ordersWithMeta
            .filter(insurance =>  orderHasFailed(insurance.insuranceName))
            .map(insurance => ({...insurance, validationErrors: getValidationErrors(insurance.insuranceName)}));

        sorted.completed = [...sorted.completed, ...completedOrders];
        sorted.pending = [...sorted.pending, ...pendingOrders];
        sorted.failed = [...sorted.failed, ...failedOrders];

        return sorted;
    }, {completed: [], pending: [], failed: []});

    const sortByNameToArray = (insurances) => {
        return insurances.reduce((acc, insurance) => {
            let existingName = acc.filter((addedName) => addedName.insuranceName === insurance.insuranceName);
            if (existingName.length) {
                let tmp = existingName[0].insurances;
                existingName[0].insurances = [tmp];
                existingName[0].insurances.push(insurance);
            } else {
                acc.push({
                    insuranceName: insurance.insuranceName,
                    insurances: insurance
                });
            }

            return acc;
        }, []);
    };

    if (orderSystemDown) {
        return {
            completed: [],
            pending: sortByNameToArray([
                ...sorted.completed,
                ...sorted.pending,
                ...sorted.failed
            ]),
            failed: []
        };
    }

    const completed = sortByNameToArray(sorted.completed);
    const pending = sortByNameToArray(sorted.pending);
    const failed = sortByNameToArray(sorted.failed);

    return {
        completed,
        pending,
        failed,
        all: [...sorted.completed, ...sorted.pending, ...sorted.failed]
    };
}

// there can only be one initiated HD draft active for each insurance group => hide others if one has been initiated
export function filterRegistrationsIfDraftExists(regGroup) {
    let draft = regGroup.insurances
        .find(registration => registration.hasDraft);

    if (draft) {
        return {
            ...regGroup,
            insurances: [draft]
        };
    }

    return regGroup;
}

// there can only be one initiated HD draft active for each insurance object and group => hide others if one has been initiated
export function sortByObjectIdAndFilterDrafts(regGroup) {
    let sortedGroups = regGroup.map(group => {
        let grouped = groupBy(group.insurances, 'insuranceObjectId');

        group.insurances = Object.keys(grouped).map(k => {
            let groupedRegs = grouped[k];
            let draft = groupedRegs.find(reg => reg.hasDraft);

            if (draft) {
                return [draft];
            }

            return groupedRegs;
        });

        return group;
    });

    return sortedGroups;
}

export function sortBreedsById(breedData) {
    return Object.keys(breedData).reduce((byType, typeKey) => {
        let breedsForType = breedData[typeKey];

        byType[typeKey] = breedsForType.reduce((byBreedId, breed) => {
            byBreedId[breed.id] = breed.name;
            return byBreedId;
        }, {});

        return byType;
    }, {});
}

export function getRelatedGroupsForInsurance(insuranceName) {
    return Object.keys(insuranceGroupSettings)
        .filter(groupKey =>
            insuranceGroupSettings[groupKey]
                .availableForInsurances.indexOf(insuranceName) > -1
        );
}

export function setAnswersRecursively(group) {
    if (!group.questions || !group.questions.length) {
        return group;
    }

    group.questions.forEach((question) => {
        if (!group.answers || !group.answers.length) {
            return;
        }
        const answer = group.answers[0].filter((answer) => { return answer.questionId === question.id});

        if (answer.length) {
            question.value = answer[0].value;
        }

        if (question.subQuestionGroup) {
            question.subQuestionGroup = setAnswersRecursively(question.subQuestionGroup); 
        }
    });

    return group;
}


